import { ChannelType } from '../../../enums/ChannelType';
import { AiPromptFeatures, AiPromptFormState, ChatGPTSettings } from '../../../types/Ai';

export interface AiPromptStreamRequest {
    contactId: number;
    channelType: ChannelType;
    channelIndexId?: number;
    websocketConnectionId?: string;
}

export interface UpdateAiPromptRequest {
    promptId: number;
    title: string;
    description: string | undefined;
    category: string | undefined;
    clientId: number | undefined;
    userName: string | undefined;
    prompt: string;
    gptSettings: ChatGPTSettings;
    feature: AiPromptFeatures;
    serializedFunctions?: string | undefined;
    timelineFunctionKeysCsv: string | undefined;
    summaryFunctionKeysCsv: string | undefined;
}

export interface CreateAiPromptRequest {
    title: string;
    description: string | undefined;
    category: string | undefined;
    prompt: string;
    clientId: number | undefined;
    userName: string | undefined;
    gptSettings: ChatGPTSettings;
    feature: AiPromptFeatures;
    serializedFunctions: string | undefined;
    timelineFunctionKeysCsv: string | undefined;
    summaryFunctionKeysCsv: string | undefined;
}

export interface ChatReplyResponse {
    contactId: number;
    message: string;
    functionData: {
        [key: string]: string | string[];
    };
}

export interface CallSummaryResponse {
    transcript: string;
    transcriptSegments: string;
    summary: {
        [key: string]: string | string[];
    };
}

export const buildUpdateAiPromptRequest = (
    promptId: number,
    formState: AiPromptFormState,
): UpdateAiPromptRequest => {
    return {
        promptId: promptId,
        title: formState.title,
        description: formState.description,
        category: formState.category,
        prompt: formState.prompt,
        gptSettings: formState.gptSettings,
        feature: formState.feature,
        serializedFunctions: formState.serializedFunctions,
        summaryFunctionKeysCsv: formState.summaryFunctionKeysCsv,
        timelineFunctionKeysCsv: formState.timelineFunctionKeysCsv,
        clientId: formState.clientId ? Number.parseInt(formState.clientId) : undefined,
        userName: formState.userName || undefined, //important to not send empty string
    };
};

export const buildCreateAiPromptRequest = (formState: AiPromptFormState): CreateAiPromptRequest => {
    return {
        title: formState.title,
        description: formState.description,
        category: formState.category,
        prompt: formState.prompt,
        clientId: formState.clientId ? Number.parseInt(formState.clientId) : undefined,
        userName: formState.userName || undefined, //important to not send empty string
        gptSettings: formState.gptSettings,
        feature: formState.feature,
        summaryFunctionKeysCsv: formState.summaryFunctionKeysCsv,
        timelineFunctionKeysCsv: formState.timelineFunctionKeysCsv,
        serializedFunctions: formState.serializedFunctions,
    };
};

export interface AiActivateSmartReplyRequest {
    contactId: number;
    planId: number;
    channelIndexId: number | undefined;
    userName: string | undefined;
}

export interface AiSmartReplyPlan {
    planId: number;
    clientId: number | undefined;
    userName: string | undefined;
    name: string;
    description: string;
    dateCreated: string;
    dateUpdated: string;
    followUpDelay: number;
}

export interface AiSmartReplyPlanItem {
    planItemId: number;
    planId: number;
    parentPlanItemId: number | undefined;
    promptId: number;
    dateCreated: string;
}

export interface CreateAiSmartReplyPlanRequest {
    clientId: number | undefined;
    userName: string | undefined;
    name: string;
    description: string;
    followUpDelay: number;
    completionPromptId: number | undefined;
    expiresMins: number | undefined;
}

export const buildCreateAiSmartReplyPlanRequest = (request: CreateAiSmartReplyPlanRequest) => {
    return {
        clientId: request.clientId,
        userName: request.userName,
        name: request.name,
        description: request.description,
        followUpDelay: request.followUpDelay,
        completionPromptId:
            request.completionPromptId === 0 ? undefined : request.completionPromptId,
        expiresMins: request.expiresMins === 0 ? undefined : request.expiresMins,
    };
};

export interface EditAiSmartReplyPlanRequest {
    planId: number;
    clientId: number | undefined;
    userName: string | undefined;
    name: string;
    description: string;
    followUpDelay: number;
    completionPromptId: number | undefined;
    expiresMins: number | undefined;
}

export const buildEditAiSmartReplyPlanRequest = (request: EditAiSmartReplyPlanRequest) => {
    return {
        planId: request.planId,
        clientId: request.clientId,
        userName: request.userName,
        name: request.name,
        description: request.description,
        followUpDelay: request.followUpDelay,
        completionPromptId:
            request.completionPromptId === 0 ? undefined : request.completionPromptId,
        expiresMins: request.expiresMins === 0 ? undefined : request.expiresMins,
    };
};

export interface CreateAiSmartReplyPlanItemRequest {
    planId: number;
    parentPlanItemId: number | undefined;
    prompt: string;
    name: string;
    description: string | undefined;
    clientId: number | undefined;
    userName: string | undefined;
    repeats: boolean;
    repeatLimit: number | undefined;
}

export interface RequestPauseSmartReply {
    contactId: number;
    contactPlanId: number;
}

export interface RequestSmartReplySimulateWebhook {
    contactId: number;
    channelIndexId: number;
}

export interface PlanItemRequestQuery {
    planId: number;
    planItemId: number;
}
